/**
 * Tokens de pago de cada cargo (ENA-1607). Payway exige un token de pago NUEVO por operación:
 * - Tarjeta guardada (GET): token permanente + CVV → el SDK genera el token de pago ahora.
 * - Tarjeta nueva de la sesión: el token de pago ya se generó al agregarla (un solo uso, menos de
 *   10 minutos); se usa tal cual si no venció.
 * Todo esto ocurre ANTES de `initiate`: si falla no hubo cobro y es seguro reintentar.
 */
import { PaymentTokenError } from '../domain/errors';
import type { SavedCard } from '../domain/models';
import type { CardTokenizer } from '../infrastructure/paywayTokenizer';
import type { CardChargeWithInstallments } from './processPayment';

/** Vigencia asumida del token de pago de una tarjeta nueva (backend: "menos de 10 minutos"). */
export const PAYMENT_TOKEN_TTL_MS = 8 * 60 * 1000;

export interface ChargeInput extends CardChargeWithInstallments {
  /** CVV ingresado para una tarjeta guardada (las tarjetas nuevas ya lo incluyen en su token). */
  securityCode?: string;
}

export interface ChargeWithPaymentToken extends CardChargeWithInstallments {
  /** Token de pago que se envía en `initiate`. */
  paymentToken: string;
}

/** ¿Tarjeta nueva de la sesión? (su `token` ya es un token de pago, no hace falta CVV). */
export const isSessionCard = (card: SavedCard | undefined): boolean =>
  card?.paymentTokenCreatedAt !== undefined;

export const isPaymentTokenExpired = (card: SavedCard, now: number = Date.now()): boolean =>
  card.paymentTokenCreatedAt !== undefined && now - card.paymentTokenCreatedAt >= PAYMENT_TOKEN_TTL_MS;

export const resolvePaymentTokens = async (
  tokenizer: CardTokenizer,
  charges: ChargeInput[],
  now: number = Date.now(),
): Promise<ChargeWithPaymentToken[]> => {
  // Validar todo antes de llamar al SDK, para no generar tokens que luego no se usen.
  charges.forEach(({ card, securityCode }) => {
    if (isSessionCard(card)) {
      if (isPaymentTokenExpired(card, now)) {
        throw new PaymentTokenError('expired');
      }
    } else if (!/^\d{3,4}$/.test(securityCode ?? '')) {
      throw new PaymentTokenError('missing-cvv');
    }
  });

  return Promise.all(
    charges.map(async ({ securityCode, ...charge }) => {
      const paymentToken = isSessionCard(charge.card)
        ? charge.card.token
        : (await tokenizer.tokenizeSavedCard(charge.card.token, securityCode ?? '')).token;
      return { ...charge, paymentToken };
    }),
  );
};
